import { useState, useEffect } from "react";

export type DeviceType = "mobile" | "tablet" | "desktop";

export function detectDeviceType(): DeviceType {
  const hasNavigator = typeof navigator !== "undefined";
  const hasWindow = typeof window !== "undefined";

  const ua = hasNavigator ? navigator.userAgent : "";
  const nav = hasNavigator ? (navigator as Navigator & { userAgentData?: { mobile?: boolean } }) : undefined;

  // Baseline mobile detection
  // Mobile: iPhone, iPod, generic Phone, or Android with "Mobile"
  const uaSaysMobile = /Mobi|Android.*Mobile|iPhone|iPod|Phone/i.test(ua);
  const uaDataMobile = nav?.userAgentData?.mobile === true;

  // Tablet hints
  // Tablet: iPad, generic Tablet, or Android without "Mobile"
  const uaSaysTablet = /iPad|Tablet/i.test(ua) || /Android(?!.*Mobile)/i.test(ua);

  // iPad in desktop mode (often reports as Mac)
  const iPadLike = /Macintosh/i.test(ua) && (hasNavigator ? (navigator.maxTouchPoints || 0) > 1 : false);

  // Viewport-based heuristic as a fallback (never trust solely, only for tie-breaks)
  const vw = hasWindow ? Math.min(window.innerWidth, window.screen?.width || window.innerWidth) : 1200;

  // Decide using a priority order
  if (uaSaysMobile || uaDataMobile) return "mobile";
  if (uaSaysTablet || iPadLike) return "tablet";
  // Heuristic fallback: widths < 600 mobile, 600-1024 tablet
  if (!hasNavigator) {
    if (vw < 600) return "mobile";
    if (vw < 1024) return "tablet";
  }
  return "desktop";
}

export function useDeviceType(): DeviceType {
  const [device, setDevice] = useState<DeviceType>(() => detectDeviceType());

  useEffect(() => {
    const computeAndSet = () => setDevice(detectDeviceType());
    computeAndSet();
    // Update on resize/orientation changes to be a bit more adaptive
    window.addEventListener("resize", computeAndSet);
    window.addEventListener("orientationchange", computeAndSet as EventListener);
    return () => {
      window.removeEventListener("resize", computeAndSet);
      window.removeEventListener("orientationchange", computeAndSet as EventListener);
    };
  }, []);

  return device;
}

// Convenience alias
export const useDevice = useDeviceType;

// Default export now returns the device type ("mobile" | "tablet" | "desktop")
export default useDeviceType;
